import type {
  ProjectContentInputType,
  ProjectCreateRequestV2Type,
} from '../apis/project/post-project-v2';
import { DtoProjectType, FeatureNumericType } from '../constants/dto';

export const PROJECT_PLAN_LIMIT_I18N_KEY = 'Error.plan-limit-exceeded';
export const PROJECT_PLAN_LIMIT_ERROR_NAME = 'ProjectPlanLimitExceededError';

interface FeatureLimitNumericMap {
  [key: string]: number | null | undefined;
  [key: number]: number | null | undefined;
}

interface FindProjectPlanLimitFailureParams {
  request: ProjectCreateRequestV2Type;
  featureLimitNumericMap?: FeatureLimitNumericMap | null;
}

export interface ProjectPlanLimitFailure {
  feature: FeatureNumericType;
  requested: number;
  limit: number;
  message: string;
  i18nKey: typeof PROJECT_PLAN_LIMIT_I18N_KEY;
  i18nValues: {
    name: string;
    message: string;
    featureName: string;
    limit: string;
    current: string;
  };
}

export class ProjectPlanLimitExceededError extends Error {
  readonly failure: ProjectPlanLimitFailure;

  constructor(failure: ProjectPlanLimitFailure) {
    super(failure.message);
    this.name = PROJECT_PLAN_LIMIT_ERROR_NAME;
    this.failure = failure;
  }
}

export const isProjectPlanLimitExceededError = (
  error: unknown,
): error is ProjectPlanLimitExceededError => {
  return (
    error instanceof ProjectPlanLimitExceededError ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === PROJECT_PLAN_LIMIT_ERROR_NAME)
  );
};

const LTS_PROJECT_TYPES = new Set<number>([
  DtoProjectType.spotlight,
  DtoProjectType.soccer,
]);
const MTS_PROJECT_TYPES = new Set<number>([DtoProjectType.commerce]);

const FEATURE_LABEL: Record<FeatureNumericType, string> = {
  [FeatureNumericType.LTS_ORIGINAL_VIDEO_SIZE]: 'LTS 원본 영상 용량',
  [FeatureNumericType.LTS_ORIGINAL_VIDEO_DURATION]: 'LTS 유튜브 원본 영상 시간',
  [FeatureNumericType.MTS_ORIGINAL_VIDEO_SIZE]: 'MTS 원본 영상 용량',
  [FeatureNumericType.MTS_ORIGINAL_VIDEO_DURATION]: 'MTS 원본 영상 시간',
  [FeatureNumericType.BRAND_TEMPLATE_COUNT]: '브랜드 템플릿 수',
  [FeatureNumericType.VIDEO_RENDERING_DATE]: '내보내기 영상 보관 기간',
  [FeatureNumericType.PROJECT_DATE]: '프로젝트 보관 기간',
};

const DURATION_FEATURES = new Set<FeatureNumericType>([
  FeatureNumericType.LTS_ORIGINAL_VIDEO_DURATION,
  FeatureNumericType.MTS_ORIGINAL_VIDEO_DURATION,
]);

const SIZE_FEATURES = new Set<FeatureNumericType>([
  FeatureNumericType.LTS_ORIGINAL_VIDEO_SIZE,
  FeatureNumericType.MTS_ORIGINAL_VIDEO_SIZE,
]);

const getLimit = (
  featureLimitNumericMap: FeatureLimitNumericMap | null | undefined,
  feature: FeatureNumericType,
): number | null => {
  const value = featureLimitNumericMap?.[feature];
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
};

const getLocalFileDuration = (content: ProjectContentInputType): number => {
  if (content.type !== 'LOCAL_FILE') return 0;
  if (!('duration' in content.content)) return 0;

  const { duration } = content.content;
  return typeof duration === 'number' && Number.isFinite(duration)
    ? duration
    : 0;
};

const getContentDuration = (content: ProjectContentInputType): number => {
  if (content.type === 'URL_CONTENT') {
    return Number.isFinite(content.duration) ? content.duration : 0;
  }
  return getLocalFileDuration(content);
};

const getTotalLocalFileSize = (contents: ProjectContentInputType[]): number => {
  return contents.reduce((sum, content) => {
    if (content.type !== 'LOCAL_FILE') return sum;
    return sum + (Number.isFinite(content.size) ? content.size : 0);
  }, 0);
};

const formatDurationLimit = (valueMs: number): string => {
  const totalSeconds = Math.max(0, Math.round(valueMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0 && (minutes > 0 || seconds > 0)) {
    return [
      `${hours}시간`,
      minutes > 0 ? `${minutes}분` : null,
      seconds > 0 ? `${seconds}초` : null,
    ]
      .filter(Boolean)
      .join(' ');
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  if (seconds === 0) return `${totalMinutes}분`;
  if (totalMinutes > 0) return `${totalMinutes}분 ${seconds}초`;
  return `${seconds}초`;
};

const formatSizeLimit = (valueBytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, valueBytes);
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const normalized = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(1);
  return `${normalized}${units[unitIndex]}`;
};

const formatFeatureValue = (
  feature: FeatureNumericType,
  value: number,
): string => {
  if (DURATION_FEATURES.has(feature)) return formatDurationLimit(value);
  if (SIZE_FEATURES.has(feature)) return formatSizeLimit(value);
  return String(value);
};

const buildFailure = (
  feature: FeatureNumericType,
  requested: number,
  limit: number,
): ProjectPlanLimitFailure => {
  const featureName = FEATURE_LABEL[feature];
  const formattedLimit = formatFeatureValue(feature, limit);
  const formattedRequested = formatFeatureValue(feature, requested);
  const message = `현재 플랜의 ${featureName} 제한은 ${formattedLimit}입니다. 현재 입력값은 ${formattedRequested}입니다.`;

  return {
    feature,
    requested,
    limit,
    message,
    i18nKey: PROJECT_PLAN_LIMIT_I18N_KEY,
    i18nValues: {
      name: featureName,
      message,
      featureName,
      limit: formattedLimit,
      current: formattedRequested,
    },
  };
};

const findFirstDurationFailure = (
  contents: ProjectContentInputType[],
  feature: FeatureNumericType,
  limit: number,
  predicate: (content: ProjectContentInputType) => boolean,
): ProjectPlanLimitFailure | null => {
  for (const content of contents) {
    if (!predicate(content)) continue;

    const duration = getContentDuration(content);
    if (duration > limit) {
      return buildFailure(feature, duration, limit);
    }
  }
  return null;
};

const findSizeFailure = (
  contents: ProjectContentInputType[],
  feature: FeatureNumericType,
  limit: number,
): ProjectPlanLimitFailure | null => {
  const totalSize = getTotalLocalFileSize(contents);
  if (totalSize > limit) {
    return buildFailure(feature, totalSize, limit);
  }
  return null;
};

export const findProjectPlanLimitFailure = ({
  request,
  featureLimitNumericMap,
}: FindProjectPlanLimitFailureParams): ProjectPlanLimitFailure | null => {
  const contents = request.contents ?? [];
  if (contents.length === 0) return null;

  if (LTS_PROJECT_TYPES.has(request.type)) {
    const durationLimit = getLimit(
      featureLimitNumericMap,
      FeatureNumericType.LTS_ORIGINAL_VIDEO_DURATION,
    );
    if (durationLimit !== null) {
      const durationFailure = findFirstDurationFailure(
        contents,
        FeatureNumericType.LTS_ORIGINAL_VIDEO_DURATION,
        durationLimit,
        (content) => content.type === 'URL_CONTENT',
      );
      if (durationFailure) return durationFailure;
    }

    const sizeLimit = getLimit(
      featureLimitNumericMap,
      FeatureNumericType.LTS_ORIGINAL_VIDEO_SIZE,
    );
    if (sizeLimit !== null) {
      return findSizeFailure(
        contents,
        FeatureNumericType.LTS_ORIGINAL_VIDEO_SIZE,
        sizeLimit,
      );
    }
  }

  if (MTS_PROJECT_TYPES.has(request.type)) {
    const durationLimit = getLimit(
      featureLimitNumericMap,
      FeatureNumericType.MTS_ORIGINAL_VIDEO_DURATION,
    );
    if (durationLimit !== null) {
      const durationFailure = findFirstDurationFailure(
        contents,
        FeatureNumericType.MTS_ORIGINAL_VIDEO_DURATION,
        durationLimit,
        (content) =>
          content.type === 'LOCAL_FILE' || content.type === 'URL_CONTENT',
      );
      if (durationFailure) return durationFailure;
    }

    const sizeLimit = getLimit(
      featureLimitNumericMap,
      FeatureNumericType.MTS_ORIGINAL_VIDEO_SIZE,
    );
    if (sizeLimit !== null) {
      return findSizeFailure(
        contents,
        FeatureNumericType.MTS_ORIGINAL_VIDEO_SIZE,
        sizeLimit,
      );
    }
  }

  return null;
};
